import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Calendar, 
  CheckSquare, 
  Clock, 
  Link,
  ChevronDown,
  ChevronRight,
  ArrowRight
} from 'lucide-react';
import { format } from 'date-fns';
import { useModalNavigation } from '@/hooks/useModalNavigation';
import { PriorityIndicator } from './PriorityIndicator';

interface EventWithTasksItem {
  id: string;
  type: 'event' | 'task';
  title: string;
  time?: string;
  date: Date;
  priority: number;
  isOverdue?: boolean;
  status?: string;
  linkedPersons?: string[];
  assignedTo?: string[];
  event_id?: string;
}

interface EventWithTasksProps {
  event: EventWithTasksItem;
  relatedTasks?: EventWithTasksItem[];
  isTimeline?: boolean;
  showExpandToggle?: boolean;
}

export function EventWithTasks({ 
  event, 
  relatedTasks = [], 
  isTimeline = false,
  showExpandToggle = false 
}: EventWithTasksProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const { openModal } = useModalNavigation();
  
  const hasRelatedTasks = relatedTasks.length > 0;
  const overdueTasksCount = relatedTasks.filter(task => task.isOverdue).length;
  const completedTasksCount = relatedTasks.filter(task => task.status === 'completed').length;

  const handleEventClick = () => {
    openModal('viewEvent', { id: event.id });
  };

  const handleTaskClick = (taskId: string) => {
    openModal('viewTask', { id: taskId });
  };

  return (
    <div className="space-y-2">
      {/* Event */}
      <div className={`relative flex items-center gap-4 ${hasRelatedTasks && isTimeline ? 'pb-2' : ''}`}>
        {isTimeline && (
          <div className={`relative z-10 w-3 h-3 rounded-full border-2 bg-blue-500 border-blue-200`} />
        )}
        
        <div className={`flex-1 p-3 rounded-lg border transition-all cursor-pointer ${
          event.priority >= 3 
            ? 'bg-orange-50 border-orange-200 hover:bg-orange-100'
            : 'bg-panel border-panel hover:bg-panel-hover'
        }`} onClick={handleEventClick}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="p-1.5 rounded-full bg-blue-100 text-blue-600">
                <Calendar className="h-3 w-3" />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-sm truncate">{event.title}</h3>
                  <PriorityIndicator priority={event.priority} size="sm" />
                  {hasRelatedTasks && (
                    <div className="flex items-center gap-1">
                      <Link className="h-3 w-3 text-muted-foreground" />
                      <Badge variant="outline" className="text-xs px-1.5 py-0.5">
                        {relatedTasks.length} tâche{relatedTasks.length > 1 ? 's' : ''}
                      </Badge>
                      {overdueTasksCount > 0 && (
                        <Badge variant="destructive" className="text-xs px-1.5 py-0.5">
                          {overdueTasksCount} en retard
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
                
                {event.time && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span>{event.time}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {hasRelatedTasks && showExpandToggle && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 w-6 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded(!isExpanded);
                  }}
                >
                  {isExpanded ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                </Button>
              )}
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                <ArrowRight className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Related Tasks */}
      {hasRelatedTasks && isExpanded && (
        <div className="space-y-2">
          {relatedTasks.map((task, index) => (
            <div key={task.id} className={`relative flex items-center gap-4 ${isTimeline ? 'pl-4' : ''}`}>
              {isTimeline && (
                <>
                  {/* Connection line */}
                  <div className="absolute left-6 top-0 w-0.5 h-full bg-muted-foreground/20" />
                  <div className="absolute left-4 top-3 w-6 h-0.5 bg-muted-foreground/20" />
                  <div className={`relative z-10 w-2 h-2 rounded-full border bg-muted-foreground/40 border-muted-foreground/20`} />
                </>
              )}
              
              <div className={`flex-1 p-2.5 rounded-md border transition-all cursor-pointer ${
                task.isOverdue 
                  ? 'bg-red-50 border-red-200 hover:bg-red-100' 
                  : task.status === 'completed'
                    ? 'bg-green-50 border-green-200 hover:bg-green-100'
                    : 'bg-muted/30 border-muted hover:bg-muted/50'
              } ${isTimeline ? 'ml-0' : 'ml-8'}`} 
              onClick={() => handleTaskClick(task.id)}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className={`p-1 rounded-full ${
                      task.status === 'completed' 
                        ? 'bg-green-100 text-green-600' 
                        : task.isOverdue 
                          ? 'bg-red-100 text-red-600'
                          : 'bg-muted text-muted-foreground'
                    }`}>
                      <CheckSquare className="h-2.5 w-2.5" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm truncate ${
                          task.status === 'completed' ? 'line-through text-muted-foreground' : ''
                        }`}>
                          {task.title}
                        </span>
                        <PriorityIndicator priority={task.priority} size="xs" />
                        {task.isOverdue && (
                          <Badge variant="destructive" className="text-xs px-1 py-0">
                            En retard
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <Button variant="ghost" size="sm" className="h-5 w-5 p-0">
                    <ArrowRight className="h-2.5 w-2.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}